import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { DynamicPagesAPI, ICurrentRouteInfo, IRoutingMiddleware, IUrlHistoryManager } from 'feature-router'
import { UrlHistoryManagerSymbol } from 'feature-router'
import type { IFetchApi, BrowserWindow, Experiments, ILogger } from '@wix/thunderbolt-symbols'
import {
	Fetch,
	SiteFeatureConfigSymbol,
	CurrentRouteInfoSymbol,
	BrowserWindowSymbol,
	ExperimentsSymbol,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import type { DynamicPagesSiteConfig, IDynamicPagesResponseHandler, IPermissionsHandlerProvider } from './types'
import { DynamicPagesResponseHandlerSymbol, name, PermissionsHandlerProviderSymbol } from './symbols'
import {
	customRouterFetchRouteInfo,
	getCandidateRouteInfo,
	getRouterPrefix,
	getRouterSuffix,
	isUnresolvedRouteInfo,
} from './utils'
import type { IWarmupDataProvider } from 'feature-warmup-data'
import { WarmupDataProviderSymbol } from 'feature-warmup-data'
import type { RouterFetchAPI } from 'feature-router-fetch'
import { RouterFetchSymbol, RouterFetchRequestTypes } from 'feature-router-fetch'

export const DynamicPages = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		Fetch,
		UrlHistoryManagerSymbol,
		DynamicPagesResponseHandlerSymbol,
		PermissionsHandlerProviderSymbol,
		CurrentRouteInfoSymbol,
		WarmupDataProviderSymbol,
		RouterFetchSymbol,
		ExperimentsSymbol,
		LoggerSymbol,
	],
	(
		{ prefixToRouterFetchData, routerPagesSeoToIdMap, staticRoutedPageId }: DynamicPagesSiteConfig,
		browserWindow: BrowserWindow,
		fetchApi: IFetchApi,
		urlHistoryManager: IUrlHistoryManager,
		dynamicPagesResponseHandler: IDynamicPagesResponseHandler,
		permissionsHandlerProvider: IPermissionsHandlerProvider,
		currentRouteInfo: ICurrentRouteInfo,
		warmupDataProvider: IWarmupDataProvider,
		routerFetch: RouterFetchAPI,
		experiments: Experiments,
		logger: ILogger
	): IRoutingMiddleware & DynamicPagesAPI => {
		return {
			getSitemapFetchParams(routerPrefix) {
				const routerFetchData = prefixToRouterFetchData[routerPrefix]
				if (!routerFetchData) {
					return null
				}

				const relativeEncodedUrl = urlHistoryManager.getRelativeEncodedUrl()
				const queryParams = urlHistoryManager.getParsedUrl().search
				const routerSuffix = getRouterSuffix(relativeEncodedUrl)
				return routerFetch.getFetchParams(RouterFetchRequestTypes.SITEMAP, routerFetchData, {
					routerSuffix,
					queryParams,
				})
			},
			async handle(routeInfo, dynamicPageIdOverride) {
				if (isUnresolvedRouteInfo(routeInfo)) {
					const routerPrefix = getRouterPrefix(routeInfo.relativeUrl)
					const routerFetchData = prefixToRouterFetchData[routerPrefix]

					const candidateRouteInfo = await getCandidateRouteInfo(
						routeInfo,
						routerPrefix,
						routerFetchData,
						routerPagesSeoToIdMap,
						staticRoutedPageId,
						dynamicPageIdOverride,
						currentRouteInfo,
						experiments,
						warmupDataProvider
					)

					if (candidateRouteInfo) {
						return candidateRouteInfo
					}

					return customRouterFetchRouteInfo(
						routeInfo,
						routerFetch,
						dynamicPagesResponseHandler,
						fetchApi,
						routerFetchData,
						permissionsHandlerProvider,
						dynamicPageIdOverride,
						logger
					)
				}

				return routeInfo
			},
		}
	}
)
